import { GRAPESHOT_ENDPOINT } from "@app/constants";
import type { PageTargetingValue } from "@sphtech/web2-ads";

type GrapeshotChannel = {
  name?: unknown;
};

type GrapeshotResponse = {
  channels?: unknown;
  fr?: unknown;
};

function getPageUrl(urlPath?: string) {
  const path = urlPath || window.location.pathname;
  // Normalise through URL parsing so a caller-supplied path missing a leading
  // "/" (or carrying dot segments) still yields a valid same-origin URL rather
  // than a mangled "https://site.comfashion/..." string.
  return new URL(path, window.location.origin).href;
}

function getAbortSignal() {
  return "AbortSignal" in window && typeof AbortSignal.timeout === "function"
    ? AbortSignal.timeout(5000)
    : undefined;
}

function extractChannelNames(dataObject: GrapeshotResponse) {
  if (!Array.isArray(dataObject.channels)) return [];

  return dataObject.channels
    .filter(
      (channel): channel is GrapeshotChannel =>
        !!channel && typeof channel === "object" && "name" in channel,
    )
    .map((channel) => channel.name)
    .filter((name): name is string => typeof name === "string" && !!name);
}

function updateGrapeshotMeta(channels: string[]) {
  // getElementById returns only the first match; query all id="grapeshot"
  // nodes so stray duplicates (malformed markup, previous runs) are removed
  // instead of left behind.
  const all = Array.from(document.querySelectorAll("#grapeshot"));
  const meta =
    all.find((el): el is HTMLMetaElement => el instanceof HTMLMetaElement) ??
    document.createElement("meta");

  // Remove every other node squatting on the id.
  all.forEach((el) => {
    if (el !== meta) el.remove();
  });

  meta.id = "grapeshot";
  meta.name = "grapeshot_tag";
  meta.content = channels.join(",");

  // Ensure the meta lives in <head>, even if a stray copy was parented
  // elsewhere.
  if (meta.parentElement !== document.head) {
    document.head.appendChild(meta);
  }
}

export function ensureLegacyGrapeshot(urlPath?: string): Promise<void> {
  if (!GRAPESHOT_ENDPOINT || typeof window === "undefined") {
    return Promise.resolve();
  }

  const pageUrl = getPageUrl(urlPath);
  if (window.gpt_grapeshots_promise && window.gpt_grapeshots_url === pageUrl) {
    return window.gpt_grapeshots_promise;
  }

  // Build via URL/searchParams so an endpoint that already carries a query
  // string (or trailing "?") still joins the url param correctly.
  const requestUrl = new URL(GRAPESHOT_ENDPOINT, window.location.origin);
  requestUrl.searchParams.set("url", pageUrl);

  window.gpt_grapeshots_url = pageUrl;
  window.gpt_grapeshots_promise = fetch(requestUrl, {
    signal: getAbortSignal(),
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(
          `Grapeshot request failed (${response.status}${response.statusText ? ` ${response.statusText}` : ""}) for ${pageUrl}`,
        );
      }
      return response.json();
    })
    .then((data) => {
      // A newer navigation may have superseded this page while its fetch was
      // in flight; bail before mutating globals (mirrors the catch guard
      // below) so a late page A response can't clobber page B's targeting.
      if (window.gpt_grapeshots_url !== pageUrl) return;
      const dataObject =
        data && typeof data === "object" ? (data as GrapeshotResponse) : {};
      const channels = extractChannelNames(dataObject).slice(0, 100);
      // Apply the no_category fallback once so window.gpt_grapeshots and the
      // <meta id="grapeshot"> content stay in sync for any downstream reader.
      const gsChannels = channels.length ? channels : ["no_category"];

      window.gpt_grapeshots = gsChannels;
      window.gpt_grapeshots_fr = dataObject.fr;
      updateGrapeshotMeta(gsChannels);
    })
    .catch((error: unknown) => {
      // Don't cache the rejection: clear the memoised promise/url so a later
      // call for the same page can refetch instead of replaying the failure.
      if (window.gpt_grapeshots_url === pageUrl) {
        window.gpt_grapeshots_promise = undefined;
        window.gpt_grapeshots_url = undefined;
      }
      throw error;
    });

  return window.gpt_grapeshots_promise;
}

export async function readLegacyGrapeshotTargeting(
  urlPath?: string,
): Promise<Record<string, PageTargetingValue>> {
  if (typeof window === "undefined") return {};

  await ensureLegacyGrapeshot(urlPath).catch(() => undefined);

  const targeting: Record<string, PageTargetingValue> = {};
  if (Array.isArray(window.gpt_grapeshots)) {
    const channels = window.gpt_grapeshots
      .filter((channel): channel is string => typeof channel === "string")
      .slice(0, 100);
    if (channels.length > 0) {
      targeting.gs_channels = channels;
    }
  }

  const fr = window.gpt_grapeshots_fr;
  if (typeof fr === "boolean" || typeof fr === "string") {
    targeting.fr = String(fr) === "true" ? "true" : "false";
  }

  return targeting;
}
